import { api, API_ROOT_URL, ApiError, asList, DEVICE_NAME } from './client';
import { clearTokens, setTokens } from './tokens';
import type {
  AdminHotel, AdminPlan, EmailLocale, EmailPreview, EmailTemplateContent, EmailTemplateDetail, EmailTemplateKind, AdminSubscriptionRow, AssignSubscriptionInput, AdminStats, AdminUser, ChannelAdapter, ChannelCatalog, ChannelConnection, ChannexProvisionResult, ChannexUsageReport, CreatePlanInput,
  HealthResult, HotelProfile, Me, Member, Organization, Plan, PlanImpactInput, PlanImpactReport, RatePlan, RatePlanPage, RecoverBookingsResult,
  Room, RoomCategory, SaasInvoice, SessionSummary, Subscription, SyncJob, UpdatePlanInput, WebhookSubscription,
} from './types';

// ── Session ───────────────────────────────────────────────────────────────────

export const getMe = () => api<Me>('/auth/me');

interface AuthResult { accessToken: string; refreshToken: string; user: Me }

/**
 * Sign in — and only as a platform administrator.
 *
 * The backend admits any account; this app does not. After the login it asks
 * `/auth/me` and reads `isSuperAdmin` — NOT `role`: a membership wins in
 * `role`, so a platform admin who also owns an organization reads
 * `role: 'owner'` and would be locked out of the very panel the backend's
 * `SuperAdminGuard` admits them to. A non-admin's fresh session is revoked
 * before the error goes back; nothing of it is kept.
 */
export async function login(email: string, password: string): Promise<Me> {
  let result: AuthResult;
  try {
    result = await api<AuthResult>('/auth/login', {
      method: 'POST',
      body: { email, password },
      headers: { 'X-Device-Name': DEVICE_NAME },
      skipAuthRefresh: true,
    });
  } catch (err) {
    // The backend says "Invalid credentials"; the person reads Russian.
    if (ApiError.is(err) && err.statusCode === 401) throw new ApiError({ ...err, message: 'Неверный e-mail или пароль', requestId: err.requestId });
    throw err;
  }
  setTokens(result);
  const me = await getMe().catch(() => null);
  if (!me?.isSuperAdmin) {
    await api('/auth/logout', { method: 'POST', body: { refreshToken: result.refreshToken } }).catch(() => undefined);
    clearTokens();
    throw new Error('Этот вход только для администраторов платформы');
  }
  return me;
}

/** Best effort on the backend, unconditional locally: a backend that is down must not keep a browser signed in. */
export async function logout(): Promise<void> {
  const refreshToken = localStorage.getItem('da.refresh');
  await api('/auth/logout', { method: 'POST', body: refreshToken ? { refreshToken } : {} }).catch(() => undefined);
  clearTokens();
}

export const listSessions = async () => asList<SessionSummary>(await api<unknown>('/auth/sessions'), '/auth/sessions');

export const revokeSession = (jti: string) => api<{ success: true }>(`/auth/sessions/${encodeURIComponent(jti)}`, { method: 'DELETE' });

/** Revokes every OTHER session; this one keeps its tokens. */
export const changePassword = (currentPassword: string, newPassword: string) =>
  api<{ success: true }>('/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } });

export const updateMe = (body: { displayName?: string; phone?: string | null }) =>
  api<Me>('/users/me', { method: 'PATCH', body });

// ── Platform ──────────────────────────────────────────────────────────────────

export const getStats = () => api<AdminStats>('/admin/stats');

export const getChannelCatalog = () => api<ChannelCatalog>('/channels/catalog');

export type CheckState = 'ok' | 'error' | 'skipped';

export interface BackendStatus {
  apiBaseUrl: string;
  reachable: boolean;
  checks: Record<'postgres' | 'redis' | 's3', CheckState> | null;
}

/**
 * `/ready` is public and lives OUTSIDE the `/v1` prefix. It answers 503 WITH a
 * body when a dependency is down — still an answer; only a transport failure
 * (or the browser's CORS refusal) means "unreachable from here".
 */
export async function getBackendStatus(): Promise<BackendStatus> {
  const readState = (v: { status?: string } | undefined): CheckState =>
    v?.status === 'ok' || v?.status === 'skipped' ? v.status : 'error';
  try {
    const res = await fetch(`${API_ROOT_URL}/ready`, { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(10_000) });
    const body = (await res.json().catch(() => ({}))) as { checks?: Record<string, { status?: string } | undefined> };
    const raw = body.checks;
    return {
      apiBaseUrl: API_ROOT_URL,
      reachable: res.ok,
      checks: raw ? { postgres: readState(raw.postgres), redis: readState(raw.redis), s3: readState(raw.s3) } : null,
    };
  } catch {
    return { apiBaseUrl: API_ROOT_URL, reachable: false, checks: null };
  }
}

// ── Hotels ────────────────────────────────────────────────────────────────────

export const listHotels = async () => asList<AdminHotel>(await api<unknown>('/admin/hotels'), '/admin/hotels');

export const setHotelActive = (id: string, isActive: boolean) =>
  api<{ success: true }>(`/admin/hotels/${id}`, { method: 'PATCH', body: { isActive } });

// ── Users ─────────────────────────────────────────────────────────────────────

export const listUsers = async () => asList<AdminUser>(await api<unknown>('/admin/users'), '/admin/users');

/** `super_admin` is not assignable — the backend answers 400. */
export const setUserRole = (id: string, role: string) =>
  api<{ success: true }>(`/admin/users/${id}/role`, { method: 'PATCH', body: { role } });

/** Soft: sets `isActive=false`, never a hard delete (owned organizations would orphan). */
export const deactivateUser = (id: string) => api<{ success: true }>(`/admin/users/${id}`, { method: 'DELETE' });

// ── Organizations ─────────────────────────────────────────────────────────────

/** Any member may read; whether a super admin without membership may is the backend's call (403 otherwise). */
export const getOrganization = (id: string) => api<Organization>(`/organizations/${id}`);

export const getSubscription = (organizationId: string) =>
  api<Subscription>(`/organizations/${organizationId}/billing/subscription`);

// ── Plans ─────────────────────────────────────────────────────────────────────

/** Every plan INCLUDING inactive ones, with subscriber counts. */
export const listPlans = async () => asList<AdminPlan>(await api<unknown>('/admin/plans'), '/admin/plans');

/** 409 if the code is taken. */
export const createPlan = (body: CreatePlanInput) => api<Plan>('/admin/plans', { method: 'POST', body });

/** Price changes affect only NEW subscriptions — an active one renews at its locked price. */
export const updatePlan = (id: string, body: UpdatePlanInput) => api<Plan>(`/admin/plans/${id}`, { method: 'PATCH', body });

/** Dry run — writes nothing. Called before every save of an existing plan. */
export const planImpact = (id: string, body: PlanImpactInput) =>
  api<PlanImpactReport>(`/admin/plans/${id}/impact`, { method: 'POST', body });

// ── Channex cost ──────────────────────────────────────────────────────────────

/** One billing month (`YYYY-MM`). An unreconciled month answers with empty lists — "not measured", never "cost nothing". */
export const getChannexUsage = (period: string) => api<ChannexUsageReport>('/admin/channex-usage', { query: { period } });

// ── Channex, platform level (super admin) ─────────────────────────────────────

/** One global subscription covers every property on the account. 400 when the backend's env lacks the secret or public URL. */
export const registerChannexWebhook = () => api<WebhookSubscription>('/channels/channex/webhook-subscription', { method: 'POST' });

/**
 * Backfill bookings created since `since` (ISO date-time) that the revision
 * feed can no longer offer. Deliberately manual: run on a schedule it would
 * re-read the whole account forever.
 */
export const recoverChannexBookings = (since: string) => api<RecoverBookingsResult>('/channels/channex/recover', { method: 'POST', body: { since } });

/**
 * Publish one hotel on Channex: the property, a room type + rate plan per active
 * category, the connection and the mappings. Taken out of the hotel cabinet —
 * the first run creates a billable property, so it is the platform's call.
 * Idempotent, so the same call is the repair. 400 when no category has a price.
 */
export const provisionChannex = (hotelId: string) =>
  api<ChannexProvisionResult>(`/hotels/${hotelId}/channels/channex/provision`, { method: 'POST' });

export const listChannexAdapters = async () => asList<ChannelAdapter>(await api<unknown>('/channels/channex/adapters'), '/channels/channex/adapters');

// ── Hotel detail ──────────────────────────────────────────────────────────────

export const getHotel = (id: string) => api<HotelProfile>(`/hotels/${id}`);
export const listRoomCategories = async (hotelId: string) => asList<RoomCategory>(await api<unknown>(`/hotels/${hotelId}/room-categories`), 'room-categories');
export const listRooms = async (hotelId: string) => asList<Room>(await api<unknown>(`/hotels/${hotelId}/rooms`), 'rooms');
export const listRatePlans = async (hotelId: string) => {
  const page = await api<RatePlanPage | RatePlan[]>(`/hotels/${hotelId}/rate-plans`, { query: { limit: 200 } });
  return asList<RatePlan>(page, 'rate-plans');
};
export const listConnections = async (hotelId: string) => asList<ChannelConnection>(await api<unknown>(`/hotels/${hotelId}/channels`), 'channels');
export const listSyncJobs = async (hotelId: string) => asList<SyncJob>(await api<unknown>(`/hotels/${hotelId}/sync-jobs`, { query: { limit: 50 } }), 'sync-jobs');

/** Read-only probe of one connection's feed. */
export const testConnection = (hotelId: string, provider: string, connectionId: string) =>
  api<HealthResult>(`/hotels/${hotelId}/channels/${provider}/test`, { method: 'POST', query: { connectionId } });

/** Enqueue an import for one connection; the job appears in sync-jobs as `queued`. */
export const requestSync = (hotelId: string, provider: string, connectionId: string) =>
  api<unknown>(`/hotels/${hotelId}/channels/${provider}/sync`, { method: 'POST', query: { connectionId } });

export const getDemoData = (hotelId: string) => api<{ exists: boolean }>(`/hotels/${hotelId}/demo-data`);
export const seedDemoData = (hotelId: string) => api<Record<string, unknown>>(`/hotels/${hotelId}/demo-data`, { method: 'POST' });
export const clearDemoData = (hotelId: string) => api<Record<string, unknown>>(`/hotels/${hotelId}/demo-data`, { method: 'DELETE' });

// ── Organization detail ───────────────────────────────────────────────────────

export const listMembers = async (organizationId: string) => asList<Member>(await api<unknown>(`/organizations/${organizationId}/members`), 'members');
export const listInvoices = async (organizationId: string) => asList<SaasInvoice>(await api<unknown>(`/organizations/${organizationId}/billing/invoices`), 'invoices');

// ── Subscriptions set by hand ─────────────────────────────────────────────────

/** Every organization's subscription, soonest to run out first. */
export const listSubscriptions = async () =>
  asList<AdminSubscriptionRow>(await api<unknown>('/admin/subscriptions'), '/admin/subscriptions');

/** Put an organization on a plan until a day; `payment` records money taken outside the gateway. */
export const assignSubscription = (organizationId: string, body: AssignSubscriptionInput) =>
  api<Subscription>(`/admin/organizations/${organizationId}/subscription`, { method: 'PUT', body });

/** Free at once. */
export const cancelSubscription = (organizationId: string) =>
  api<Subscription>(`/admin/organizations/${organizationId}/subscription/cancel`, { method: 'POST' });

// ─── Online payments (the Montra switch) ─────────────────────────────────────
/** Off: no checkout anywhere, no renewals or dunning mails. Subscription dates still apply. */
export const getOnlinePayments = () => api<{ enabled: boolean }>('/admin/online-payments');
export const setOnlinePayments = (enabled: boolean) =>
  api<{ enabled: boolean }>('/admin/online-payments', { method: 'PUT', body: { enabled } });

// ── Email templates ───────────────────────────────────────────────────────────

const tpl = (kind: string, locale: EmailLocale) =>
  `/admin/email-templates/${encodeURIComponent(kind)}/${locale}`;

export const listEmailTemplates = async () =>
  asList<EmailTemplateKind>(await api<unknown>('/admin/email-templates'), '/admin/email-templates');
export const getEmailTemplate = (kind: string, locale: EmailLocale) => api<EmailTemplateDetail>(tpl(kind, locale));
export const saveEmailTemplate = (kind: string, locale: EmailLocale, body: EmailTemplateContent) =>
  api<EmailTemplateDetail>(tpl(kind, locale), { method: 'PUT', body });
export const resetEmailTemplate = (kind: string, locale: EmailLocale) =>
  api<void>(tpl(kind, locale), { method: 'DELETE' });
export const previewEmailTemplate = (kind: string, locale: EmailLocale, body: EmailTemplateContent) =>
  api<EmailPreview>(`${tpl(kind, locale)}/preview`, { method: 'POST', body });
export const sendTestEmailTemplate = (kind: string, locale: EmailLocale, body: EmailTemplateContent) =>
  api<{ to: string }>(`${tpl(kind, locale)}/test`, { method: 'POST', body });
